export type CsvFieldError = { field: string; message: string }
export type CsvRowError = {
  row?: number
  fieldErrors: CsvFieldError[]
}

const ROW_PREFIX = /^error invalid csv provided at row (\d+): /i
const HEADER_PREFIX = /^error invalid csv headers: /i
const FIELD_MARKER = ': Value error, '

const PLAIN_FIELD_ERRORS: Record<string, string> = {
  'cloud_product_id is required when cloud_license_arn is provided':
    'cloud_product_id',
  'cloud_account_id is required when cloud_license_arn is provided':
    'cloud_account_id',
  'cloud_product_id is required when cloud_customer_id is provided':
    'cloud_product_id',
}

const FIELD_LABELS: Record<string, string> = {
  labra_subscription_id: 'Subscription ID',
  cloud_customer_id: 'Cloud customer ID',
  cloud_product_id: 'Cloud product ID',
  cloud_license_arn: 'Cloud license ARN',
  cloud_account_id: 'Cloud account ID',
  dimension_name: 'Dimension name',
  quantity: 'Quantity',
}

function humanizeField(fieldKey: string): string {
  return FIELD_LABELS[fieldKey] ?? fieldKey.replace(/_/g, ' ')
}

function humanizeMessage(fieldKey: string, message: string): string {
  const prefix = `${fieldKey} `
  if (message.startsWith(prefix)) {
    const rest = message.slice(prefix.length)
    return rest.charAt(0).toUpperCase() + rest.slice(1)
  }
  return message
}

function plainFieldKey(part: string): string | undefined {
  if (!Object.hasOwn(PLAIN_FIELD_ERRORS, part)) return undefined
  const fieldKey = PLAIN_FIELD_ERRORS[part]
  return typeof fieldKey === 'string' ? fieldKey : undefined
}

function toFieldError(part: string): CsvFieldError {
  const markerIdx = part.indexOf(FIELD_MARKER)
  if (markerIdx !== -1) {
    const fieldKey = part.slice(0, markerIdx)
    const message = part.slice(markerIdx + FIELD_MARKER.length)
    return {
      field: humanizeField(fieldKey),
      message: humanizeMessage(fieldKey, message),
    }
  }

  const fieldKey = plainFieldKey(part)
  if (fieldKey) {
    return {
      field: humanizeField(fieldKey),
      message: humanizeMessage(fieldKey, part),
    }
  }

  return { field: part, message: part }
}

export function parseCsvUploadError(raw: string): CsvRowError | null {
  const rowMatch = raw.match(ROW_PREFIX)
  if (rowMatch) {
    return {
      row: Number(rowMatch[1]),
      fieldErrors: raw.replace(ROW_PREFIX, '').split('; ').map(toFieldError),
    }
  }

  if (!HEADER_PREFIX.test(raw)) return null

  const fieldErrors = raw
    .replace(HEADER_PREFIX, '')
    .split('; ')
    .map(part => part.trim())
    .filter(part => part.length > 0)
    .map(toFieldError)

  if (fieldErrors.length === 0) return null
  return { fieldErrors }
}

export function parseCsvUploadErrors(rawErrors: string[]): CsvRowError[] {
  return rawErrors
    .map(parseCsvUploadError)
    .filter((e): e is CsvRowError => e !== null)
}
